import { useState, useEffect, useRef, memo } from 'react';
import { Play } from 'lucide-react';
import { useNetworkQuality } from '@/hooks/useNetworkQuality';

interface VideoThumbnailProps {
  video: {
    video_url: string;
    thumbnail_url?: string | null;
    title: string;
  };
  index?: number;
  priority?: boolean; // Load immediately for above-fold content
}

export const VideoThumbnail = memo(({ video, index = 0, priority = false }: VideoThumbnailProps) => {
  const [isVisible, setIsVisible] = useState(priority);
  const [isLoaded, setIsLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { isSlow, isFast } = useNetworkQuality();

  // Adaptive rootMargin: fast wifi/4g preloads well ahead, LTE holds tighter,
  // slow / save-data waits until the card is basically visible.
  const rootMargin = isSlow ? '100px' : isFast ? '600px' : '300px';

  useEffect(() => {
    if (priority) return; // Skip observer for priority items
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [index, priority, rootMargin]);

  // If thumbnail exists, show it with optimized loading
  if (video.thumbnail_url) {
    return (
      <img
        src={video.thumbnail_url}
        alt={video.title}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-200"
      />
    );
  }

  // Otherwise, smart lazy loading — fetch only the first frame via media fragment.
  // On slow / save-data networks we skip the frame fetch entirely and keep the
  // gradient placeholder so the grid renders instantly.
  return (
    <div ref={ref} className="w-full h-full relative">
      {/* Instant gradient placeholder - always visible */}
      <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/30 via-neon-pink/20 to-neon-cyan/30 flex items-center justify-center">
        <Play className="w-12 h-12 text-white/40" />
      </div>
      
      {/* Load video only when visible AND network isn't slow / save-data */}
      {isVisible && !isSlow && (
        <video
          src={`${video.video_url}#t=0.1`}
          preload={priority || isFast ? 'metadata' : 'none'}
          muted
          playsInline
          disableRemotePlayback
          onLoadedData={() => setIsLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </div>
  );
});

VideoThumbnail.displayName = 'VideoThumbnail';

